import React, { useEffect, useState, useRef } from "react";
import { CustomHeaderWithOutAppBar } from "../../components/header/index.jsx";
import socketIOClient from "socket.io-client";
import './upgradeandpolicyagent.scss';
import { Container, Form } from 'react-bootstrap';
import url from "../../config.js";
import { toast } from "react-toastify";
import {
    Upload, FolderOpen, CheckCircle, Settings, Play, XCircle, Clock,
    FileText, AlertCircle, Download, RefreshCw, Github, Loader,
    MessageSquare, Bot, Search, Send, Shield, ChevronDown, ChevronRight
} from 'lucide-react';

const domain = url.domain;
const backendUrl = url.backendUrl;
const socketEndpoint = url.SOCKET;

// ─── Agent modes ───────────────────────────────────────────────────────────────
const AGENT_MODES = {
    MULE_UPGRADE: 'mule',
    POLICY_UPGRADE: 'policy',
};

// ─── AgentService (unchanged from MuleUpgrade) ─────────────────────────────────
class AgentService {
    getScriptedAnalysis({ currentVersion, targetVersion, fileCount, flowCount, configCount, connectors, dependencies, warnings }) {
        const lines = [];
        lines.push('📊 **Project Analysis Complete**\n');
        lines.push('**Project Summary:**');
        lines.push(`- Current Version: ${currentVersion || 'Mule 4.x'}`);
        lines.push(`- Total Files: ${fileCount}`);
        lines.push(`- Flows: ${flowCount || 'Unknown'}`);
        lines.push(`- Configurations: ${configCount || 'Unknown'}\n`);
        lines.push('**Detected Components:**');
        if (connectors?.length) {
            connectors.forEach(c => lines.push(`- ${typeof c === 'object' ? `${c.name} (v${c.version})` : c}`));
        } else {
            lines.push('- Standard HTTP/Database connectors');
        }
        lines.push('');
        if (warnings?.length) {
            lines.push('⚠️ **Warnings & Issues:**');
            warnings.forEach(w => lines.push(w));
            lines.push('');
        }
        if (fileCount > 500) lines.push('⚠️ **Large Project Warning:** Migration may take 10-15 minutes.\n');
        lines.push('**Recommendations:**');
        lines.push('- Review all custom transformations after migration');
        lines.push('- Test API endpoints thoroughly');
        lines.push('- Update connector configurations if needed');
        if (dependencies?.some(d => d.includes('apikit'))) lines.push('- APIKit may require manual updates');
        lines.push('');
        const t = fileCount > 500 ? '10-15 minutes' : fileCount > 200 ? '5-10 minutes' : '3-5 minutes';
        lines.push(`⏱️ **Estimated Migration Time:** ${t}\n`);
        lines.push('✅ Ready to proceed with migration!');
        return lines.join('\n');
    }

    getScriptedResponse(event, data = {}) {
        const map = {
            welcome_mule: "Hi! I'm your Mule Upgrade Agent. Ready to help you upgrade your Mule projects.",
            welcome_policy: "Hi! I'm your Policy Upgrade Agent. Ready to manage your Mule policy upgrades.",
            source_selected: data.sourceType === 'local' ? 'Local directory selected. Please upload your Mule project folder.'
                : data.sourceType === 'git' ? 'Git repository mode selected. Provide your repository URL.'
                    : 'Multiple repositories mode selected. Upload your repo-list.txt file.',
            upload_success: `Great! I've received ${data.fileCount || 0} files. Please add configurations to analyse.`,
            analyzing: '🔍 Analyzing your project structure, dependencies, and configurations...',
            analysis_complete: 'Analysis complete! Review the findings above. Please add the configuration and start migration.',
            git_url_set: 'Git repository configured. Ready to clone and migrate when you are.',
        };
        return map[event] || data.message || 'Processing...';
    }

    getRuntimeNotes(v) {
        return { '4.9.3': 'Latest stable with improved DataWeave performance.', '4.8.0': 'Enhanced security + bug fixes.', '4.7.0': 'LTS, recommended for enterprise.' }[v] || 'Ensure dependencies are compatible.';
    }
}

// ═══════════════════════════════════════════════════════════════════════════════
export default function CombinedUpgradeAgent() {
    // ── Agent mode ──────────────────────────────────────────────────────────────
    const [agentMode, setAgentMode] = useState(AGENT_MODES.MULE_UPGRADE);

    // ── Shared ──────────────────────────────────────────────────────────────────
    const [wsConnected, setWsConnected] = useState(false);
    const [sessionId, setSessionId] = useState(null);
    const wsRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);
    const agentService = useRef(new AgentService());

    // ── Mule Upgrade state ───────────────────────────────────────────────────────
    const fileInputRef = useRef(null);
    const configRef = useRef(null);
    const messagesEndRef = useRef(null);

    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [sourceType, setSourceType] = useState('local');
    const [gitUrl, setGitUrl] = useState('');
    const [gitBranch, setGitBranch] = useState('main');
    const [repoListFile, setRepoListFile] = useState(null);
    const [migrationStatus, setMigrationStatus] = useState('idle');
    const [projects, setProjects] = useState([]);
    const [dependencyAnalysis, setDependencyAnalysis] = useState(null);
    const [analyzingDependencies, setAnalyzingDependencies] = useState(false);
    const [userConfirmed, setUserConfirmed] = useState(false);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [analysisComplete, setAnalysisComplete] = useState(false);
    const [analysisData, setAnalysisData] = useState(null);
    const [inputMessage, setInputMessage] = useState('');
    const [uploadPath, setUploadPath] = useState('');
    const [waitingForConfirmation, setWaitingForConfirmation] = useState(false);
    const [messages, setMessages] = useState([
        { type: 'agent', content: "Hi! I'm your Mule Upgrade Agent. Ready to help you upgrade your Mule projects.", timestamp: new Date().toLocaleTimeString() }
    ]);
    const [logsHeight] = useState(300);
    const [config, setConfig] = useState({ targetRuntime: '4.9.3', targetJava: '17', targetBranch: 'main', destinationGitUrl: '', skipTests: true, dynamicVersions: true, cleanupClones: true });
    const [migrationProgress, setMigrationProgress] = useState({ percentage: 0, step: '', eta: 0 });
    const [showConfig, setShowConfig] = useState(false);
    const [summary, setSummary] = useState(null);
    const [serverPath, setServerPath] = useState('');
    const [uploadError, setUploadError] = useState('');
    const [canStartMigration, setCanStartMigration] = useState(false);

    // ── Policy Upgrade state ─────────────────────────────────────────────────────
    const logsEndRef = useRef(null);
    const [policyMigrationStatus, setPolicyMigrationStatus] = useState('idle');
    const [setupStatus, setSetupStatus] = useState('idle');
    const [logs, setLogs] = useState([]);
    const [policySummary, setPolicySummary] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploadedFilePath, setUploadedFilePath] = useState('');
    const [showPolicyConfig, setShowPolicyConfig] = useState(true);
    const [manifestPath, setManifestPath] = useState('manifests/policy-manifest.yaml');
    const [dryRun, setDryRun] = useState(false);
    const [orgId, setOrgId] = useState('');
    const [envId, setEnvId] = useState('');
    const [groupId, setGroupId] = useState('');
    const [clientId, setClientId] = useState('');
    const [clientSecret, setClientSecret] = useState('');

    // ── Anypoint account + environment dynamic state ──
    const [muleAccounts, setMuleAccounts] = useState([]);
    const [selectedMuleAccount, setSelectedMuleAccount] = useState('');
    const [connectedApps, setConnectedApps] = useState([]);
    const [selectedConnectedApp, setSelectedConnectedApp] = useState('');
    const [environments, setEnvironments] = useState([]);
    const [selectedEnvironment, setSelectedEnvironment] = useState('');
    const [loadingAccounts, setLoadingAccounts] = useState(false);
    const [loadingEnvironments, setLoadingEnvironments] = useState(false);
    const [loadingConnectedApps, setLoadingConnectedApps] = useState(false);
    const [envError, setEnvError] = useState('');

    // ── Effects ─────────────────────────────────────────────────────────────────
    useEffect(() => {
        // Force scroll to top before anything renders
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;

        connectWebSocket();
        loadSavedPolicyConfig();

        // Second pass after paint, in case layout shifts push it down
        const raf = requestAnimationFrame(() => {
            document.documentElement.scrollTop = 0;
            document.body.scrollTop = 0;
        });

        return () => {
            cancelAnimationFrame(raf);
            if (wsRef.current) wsRef.current.close();
            if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
        };
    }, []);

    // Scroll chat to bottom only, not the page
    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }, [messages]);

    useEffect(() => {
        if (logsEndRef.current) logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }, [logs]);

    // Switch agent: reset relevant UI and post welcome message
    useEffect(() => {
        if (agentMode === AGENT_MODES.MULE_UPGRADE) {
            setMessages([{ type: 'agent', content: agentService.current.getScriptedResponse('welcome_mule'), timestamp: new Date().toLocaleTimeString() }]);
        } else {
            setLogs([]);
            fetchMuleAccounts(); // ← load accounts when switching to Policy tab
        }
    }, [agentMode]);

    // ── WebSocket ────────────────────────────────────────────────────────────────
    const connectWebSocket = () => {
        try {
            const socket = socketIOClient(socketEndpoint, { rejectUnauthorized: true, transports: ['websocket'] });

            socket.on("connect", () => {
                setWsConnected(true);
                addMuleMessage('Connected to upgrade server', 'agent', 'success');
                addPolicyLog('success', 'Connected to upgrade server');
            });

            socket.on("mule-migration", (data) => handleMuleWebSocketMessage(data));
            socket.on("policy-upgradation", (data) => handlePolicyWebSocketMessage(data));

            socket.on("joined-session", (data) => {
                console.log('✅ Joined session:', data.sessionId);
            });

            socket.on("disconnect", () => {
                setWsConnected(false);
                addMuleMessage('Disconnected from upgrade server', 'agent', 'warning');
                addPolicyLog('warning', 'Disconnected from server');
                reconnectTimeoutRef.current = setTimeout(connectWebSocket, 3000);
            });

            socket.on("connect_error", () => setWsConnected(false));

            wsRef.current = socket;
        } catch (e) {
            console.error('WS error:', e);
            setWsConnected(false);
        }
    };

    // ── Mule helpers ─────────────────────────────────────────────────────────────
   const addMuleMessage = (content, type = 'agent', logType = 'info') => {
    setMessages(prev => {
        const last = prev[prev.length - 1];
        if (last && last.content === content && last.type === type) return prev;
        return [...prev, { type, content, timestamp: new Date().toLocaleTimeString(), logType }];
    });
};

    const handleMuleWebSocketMessage = async (message) => {
        switch (message.type) {
            case 'log': addMuleMessage(message.data.message, 'agent', message.data.type); break;
            case 'analysis-complete':
                setAnalyzingDependencies(false);
                setDependencyAnalysis(true);
                setWaitingForConfirmation(true);
                break;
            case 'project': updateProjectStatus(message.data); break;
            case 'summary': setSummary(message.data); break;
            case 'progress': setMigrationProgress(message.data); break;
            case 'complete':
                setMigrationStatus(message.data.success ? 'completed' : 'failed');
                if (message.data.summary) setSummary(message.data.summary);
                addMuleMessage(message.data.success ? 'All done! Projects upgraded.' : 'Upgrade failed.', 'agent', message.data.success ? 'success' : 'error');
                setMigrationProgress({ percentage: 100, step: 'Complete', eta: 0 });
                break;
            case 'error':
                addMuleMessage(message.data.message, 'agent', 'error');
                if (message.data.fatal) setMigrationStatus('failed');
                break;
        }
    };

    const updateProjectStatus = (projectData) => {
        setProjects(prev => {
            const existing = prev.find(p => p.name === projectData.name);
            return existing ? prev.map(p => p.name === projectData.name ? { ...p, ...projectData } : p) : [...prev, projectData];
        });
    };

    const analyzeProject = async (projectPath, fileCount) => {
        setIsAnalyzing(true);
        setAnalysisComplete(false);
        addMuleMessage(agentService.current.getScriptedResponse('analyzing'), 'agent', 'info');
        try {
            const res = await fetch(backendUrl + '/api/v1/analyze-project', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'x-access-token': localStorage.getItem('token') },
                body: JSON.stringify({ projectPath, targetVersion: config.targetRuntime })
            });
            const result = await res.json();
            if (result.success) {
                setAnalysisData(result.analysis);
                addMuleMessage(agentService.current.getScriptedAnalysis({ ...result.analysis, targetVersion: config.targetRuntime, fileCount }), 'agent', 'info');
                addMuleMessage(agentService.current.getScriptedResponse('analysis_complete'), 'agent', 'success');
                setAnalysisComplete(true);
                setCanStartMigration(true);
                setShowConfig(true);
                setTimeout(() => configRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 500);
            } else throw new Error(result.error);
        } catch (e) {
            if (sourceType === 'local') addMuleMessage(`Analysis failed: ${e.message}. You can still proceed.`, 'agent', 'warning');
            setAnalysisComplete(true);
            setCanStartMigration(true);
        } finally {
            setIsAnalyzing(false);
        }
    };

    const analyzeDependencies = async () => {
        setDependencyAnalysis(null);
        setUserConfirmed(false);
        setCanStartMigration(true);
        setWaitingForConfirmation(false);
        setAnalyzingDependencies(true);
        try {
            const res = await fetch(domain + '/api/v1/ai-analysis', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'x-access-token': localStorage.getItem('token') },
                body: JSON.stringify({ sourceType, localPath: sourceType === 'local' ? serverPath : null, gitUrl: sourceType === 'git' ? gitUrl : null, gitBranch: sourceType === 'git' ? gitBranch : null, repoListPath: sourceType === 'multi-git' ? serverPath : null, projectPath: serverPath, muleVersion: config.targetRuntime, javaVersion: config.targetJava, useDynamicVersions: config.dynamicVersions })
            });
            const data = await res.json();
            if (!data.success) throw new Error(data.error || 'Analysis failed');
        } catch (e) {
            addMuleMessage(`❌ Failed to start analysis: ${e.message}`, 'agent', 'error');
            setAnalyzingDependencies(false);
        }
    };

    const handleMuleFileUpload = async (event) => {
        const files = Array.from(event.target.files);
        if (!files.length) return;
        setIsUploading(true);
        setUploadError('');
        setUploadedFiles(files);
        setAnalysisComplete(false);
        setCanStartMigration(false);
        addMuleMessage(`Uploading ${files.length} files to server...`, 'agent', 'info');
        try {
            const formData = new FormData();
            const filePaths = [];
            files.forEach(f => { filePaths.push(f.webkitRelativePath || f.name); formData.append('files', f); });
            formData.append('filePaths', JSON.stringify(filePaths));
            const res = await fetch(backendUrl + '/api/v1/upload-project', { method: 'POST', body: formData });
            if (!res.ok) throw new Error(`Upload failed: ${res.statusText}`);
            const result = await res.json();
            if (result.success) {
                setServerPath(result.projectPath);
                addMuleMessage(agentService.current.getScriptedResponse('upload_success', { fileCount: result.fileCount }), 'agent', 'success');
                if (sourceType === 'local') await analyzeProject(result.projectPath, result.fileCount);
                else setAnalysisComplete(true);
            } else throw new Error(result.error || 'Upload failed');
        } catch (e) {
            setUploadError(e.message);
            setUploadedFiles([]);
            setServerPath('');
            addMuleMessage(`Upload failed: ${e.message}`, 'agent', 'error');
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleSourceClick = async (source) => {
        setSourceType(source);
        setUploadedFiles([]);
        setServerPath('');
        setUploadError('');
        setCanStartMigration(false);
        if (source !== 'local') { setAnalysisComplete(true); setCanStartMigration(true); }
        addMuleMessage(agentService.current.getScriptedResponse('source_selected', { sourceType: source }), 'agent', 'info');
    };

    const handleConfigChange = async (field, value) => {
        setConfig(prev => ({ ...prev, [field]: value }));
        if (dependencyAnalysis) {
            setDependencyAnalysis(null);
            addMuleMessage(`⚙️ Configuration updated: **${field}**\n\n⚠️ Previous analysis is outdated. Type "analyze dependencies" to re-run.`, 'agent', 'warning');
        }
    };

    const handleUserMessage = async (message) => {
        const normalized = message.toLowerCase().trim();
        if (waitingForConfirmation) {
            const yes = ['confirm', 'yes', 'start', 'proceed', 'go', 'ok'].some(k => normalized.includes(k));
            const no = ['cancel', 'no', 'wait', 'stop', 'abort', 'change'].some(k => normalized.includes(k));
            if (yes) {
                setWaitingForConfirmation(false); setUserConfirmed(true); setCanStartMigration(true);
                addMuleMessage(message, 'user');
                addMuleMessage('🚀 **Migration confirmed! Starting now...**', 'agent', 'success');
                setMigrationStatus('running');
                setTimeout(startMuleMigration, 1000);
                return;
            }
            if (no) {
                setWaitingForConfirmation(false); setDependencyAnalysis(null); setUserConfirmed(false); setCanStartMigration(false);
                addMuleMessage(message, 'user');
                addMuleMessage('✋ **Migration paused**\n\nAdjust configuration and type "analyze dependencies" to re-run.', 'agent', 'info');
                setTimeout(() => configRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 500);
                return;
            }
            addMuleMessage(message, 'user');
            addMuleMessage('Type "**confirm**" to proceed or "**cancel**" to abort.', 'agent', 'warning');
            return;
        }
        addMuleMessage(message, 'user');
        if (normalized.includes('analyze') || normalized.includes('analyse')) {
            if (!analysisComplete) { addMuleMessage('⚠️ Upload a project first.', 'agent', 'warning'); return; }
            await analyzeDependencies(); return;
        }
        if (normalized.includes('help') || normalized === '?') {
            addMuleMessage('💡 **Commands:** "analyze dependencies", "confirm", "cancel", "config", "help"', 'agent', 'info'); return;
        }
        addMuleMessage('🤔 Type "**help**" for available commands.', 'agent', 'info');
    };

    const startMuleMigration = async () => {
        if (!wsConnected) { addMuleMessage('Not connected to server.', 'agent', 'error'); return; }
        if (!analysisComplete) { addMuleMessage('Wait for analysis to complete.', 'agent', 'warning'); return; }
        setMigrationStatus('running'); setProjects([]); setSummary(null);
        try {
            const res = await fetch(domain + '/api/v1/muleupgrade', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'x-access-token': localStorage.getItem('token') },
                body: JSON.stringify({ sourceType, localPath: sourceType === 'local' ? serverPath : null, gitUrl: sourceType === 'git' ? gitUrl : null, gitBranch: sourceType === 'git' ? gitBranch : null, repoListPath: sourceType === 'multi-git' ? serverPath : null, config, analysisData })
            });
            const result = await res.json();
            if (result?.success) {
                setSessionId(result.sessionId);
                addMuleMessage('Upgrade started. Waiting for updates...', 'agent', 'success');
                if (result.sessionId && wsRef.current) {
                    wsRef.current.emit('join-session', result.sessionId);
                }
            } else {
                addMuleMessage(`Failed to start upgrade: ${result.error || 'Unknown error'}`, 'agent', 'error');
                setMigrationStatus('idle');
            }
        } catch (e) {
            addMuleMessage(`Failed: ${e.message}`, 'agent', 'error');
            setMigrationStatus('idle');
        }
    };

    const downloadProject = async (projectName) => {
        try {
            addMuleMessage(`Downloading ${projectName}...`, 'agent', 'info');
            const res = await fetch(`${domain}/api/download/${projectName}`, { headers: { 'x-access-token': localStorage.getItem('token') } });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const blob = await res.blob();
            const a = Object.assign(document.createElement('a'), { href: URL.createObjectURL(blob), download: `${projectName}.zip` });
            document.body.appendChild(a); a.click(); document.body.removeChild(a);
            addMuleMessage(`Downloaded ${projectName}.zip!`, 'agent', 'success');
        } catch (e) { addMuleMessage(`Download failed: ${e.message}`, 'agent', 'error'); }
    };

    // ── Policy helpers ───────────────────────────────────────────────────────────
    const addPolicyLog = (type, message) => {
        setLogs(prev => [...prev, { type, message, timestamp: new Date().toLocaleTimeString() }]);
    };

    const handlePolicyWebSocketMessage = (message) => {
        switch (message.type) {
            case 'log': addPolicyLog(message.data.type, message.data.message); break;
            case 'summary': setPolicySummary(message.data); break;
            case 'complete':
                setSetupStatus(message.data.success ? 'completed' : 'failed');
                if (message.data.summary) setPolicySummary(message.data.summary);
                break;
        }
    };

    const loadSavedPolicyConfig = () => {
        try {
            const saved = localStorage.getItem('policyManagerConfig');
            if (saved) {
                const c = JSON.parse(saved);
                setManifestPath(c.manifestPath || 'manifests/policy-manifest.yaml');
            }
        } catch (e) { console.error(e); }
    };

    // ── Fetch Mule accounts from DB (same pattern as other pages) ──────────────
    const fetchMuleAccounts = async () => {
        setLoadingAccounts(true);
        try {
            const res = await fetch(`${domain}/api/v1/mule`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'x-access-token': localStorage.getItem('token')
                }
            });
            const data = await res.json();
            setMuleAccounts(data.mule || []);
        } catch (e) {
            console.error('Failed to fetch Mule accounts:', e);
        } finally {
            setLoadingAccounts(false);
        }
    };

    // ── When an Anypoint account is selected → fetch its environments ──────────
    const fetchEnvironments = async (account) => {
        if (!account) return;
        setLoadingEnvironments(true);
        setEnvError('');
        setEnvironments([]);
        setSelectedEnvironment('');
        setEnvId('');
        try {
            const res = await fetch(`${domain}/api/v1/mule/org/environments`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-access-token': localStorage.getItem('token')
                },
                body: JSON.stringify({
                    muleOrgId: account.muleOrgId,
                    connAppClientId: account.connAppClientId,
                    connAppClientSecret: account.connAppClientSecret
                })
            });
            const data = await res.json();
            const envList = data.data || data.environments || [];
            if (envList.length > 0) {
                setEnvironments(envList.map(env => ({
                    id: env.id,
                    name: env.name,
                    type: env.type,
                    isProduction: env.isProduction,
                    clientId: env.clientId,
                    organizationId: env.organizationId
                })));
            } else {
                setEnvError(data.error || 'No environments found');
            }
        } catch (e) {
            setEnvError('Failed to fetch environments');
        } finally {
            setLoadingEnvironments(false);
        }
    };

    // ── When an Anypoint account is selected → populate connected apps ─────────
    const handleMuleAccountChange = (accountId) => {
        setSelectedMuleAccount(accountId);
        setSelectedConnectedApp('');
        setSelectedEnvironment('');
        setEnvironments([]);
        setEnvId('');
        setClientId('');
        setClientSecret('');

        if (!accountId) return;

        const account = muleAccounts.find(a => a._id === accountId);
        if (!account) return;

        // Populate org fields from selected account
        setOrgId(account.muleOrgId || '');
        setGroupId(account.muleOrgId || '');

        // Build connected apps list from the account
        const apps = [];
        if (account.connAppClientId) {
            apps.push({
                id: account._id,
                label: account.muleOrg || 'Connected App',
                clientId: account.connAppClientId,
                clientSecret: account.connAppClientSecret
            });
        }
        setConnectedApps(apps);

        // Auto-select if only one connected app
        if (apps.length === 1) {
            setSelectedConnectedApp(apps[0].id);
            setClientId(apps[0].clientId);
            setClientSecret(apps[0].clientSecret);
        }

        // Fetch environments for this account
        fetchEnvironments(account);
    };

    const handleConnectedAppChange = (appId) => {
        setSelectedConnectedApp(appId);
        const app = connectedApps.find(a => a.id === appId);
        if (app) {
            setClientId(app.clientId);
            setClientSecret(app.clientSecret);
        }
    };

    const handleEnvironmentChange = (envName) => {
        setSelectedEnvironment(envName);
        const env = environments.find(e => e.name === envName);
        if (env) setEnvId(env.id);
    };

    const savePolicyConfig = () => {
        localStorage.setItem('policyManagerConfig', JSON.stringify({ manifestPath, orgId, envId, groupId, clientId }));
        addPolicyLog('success', '✓ Configuration saved locally');
    };

    const isPolicyConfigValid = () =>
        manifestPath.trim() && orgId.trim() && envId.trim() &&
        clientId.trim() && clientSecret.trim() && selectedEnvironment.trim();

    const handlePolicyFileUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;
        setSelectedFile(file);
        addPolicyLog('info', `Uploading: ${file.name}...`);
        try {
            const formData = new FormData();
            formData.append('files', file);
            formData.append('filePaths', JSON.stringify([file.name]));
            const res = await fetch(backendUrl + '/api/v1/upload-project', { method: 'POST', headers: { 'x-access-token': localStorage.getItem('token') }, body: formData });
            const result = await res.json();
            if (result.success) {
                const path = `${result.projectId}/${file.name}`;
                setUploadedFilePath(path); setManifestPath(path);
                addPolicyLog('success', `✓ Uploaded: ${path}`);
            } else { addPolicyLog('error', result.error || 'Upload failed'); setSelectedFile(null); }
        } catch (e) { addPolicyLog('error', `Upload failed: ${e.message}`); setSelectedFile(null); }
    };

    const startPolicySetup = async () => {
        if (!isPolicyConfigValid()) { addPolicyLog('error', 'Please complete all configuration fields'); return; }
        if (!wsConnected) { addPolicyLog('error', 'Not connected to server'); return; }
        setSetupStatus('running'); setLogs([]); setPolicySummary(null);
        try {
            const res = await fetch(domain + '/api/v1/upgradesetup', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'x-access-token': localStorage.getItem('token') },
                body: JSON.stringify({ manifestPath: manifestPath.trim(), dryRun, orgId: orgId.trim(), envId: envId.trim(), groupId: groupId.trim(), clientId: clientId.trim(), clientSecret: clientSecret.trim() })
            });
            const result = await res.json();
            if (result.success) { setSessionId(result.sessionId); addPolicyLog('success', 'Setup started'); }
            else { addPolicyLog('error', result.message || 'Failed to start setup'); setSetupStatus('idle'); }
        } catch (e) { addPolicyLog('error', `Failed: ${e.message}`); setSetupStatus('idle'); }
    };

    const startPolicyUpgrade = async () => {
        if (!wsConnected) { addPolicyLog('error', 'Not connected'); return; }
        setPolicyMigrationStatus('running'); setLogs([]); setPolicySummary(null);
        try {
            const res = await fetch(domain + '/api/v1/policyupgrade', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'x-access-token': localStorage.getItem('token') },
                body: JSON.stringify({ manifestPath: manifestPath.trim(), dryRun, orgId: orgId.trim(), envId: envId.trim(), groupId: groupId.trim(), clientId: clientId.trim(), clientSecret: clientSecret.trim() })
            });
            const result = await res.json();
            if (result.success) { setSessionId(result.sessionId); addPolicyLog('success', 'Upgrade started'); }
            else { addPolicyLog('error', result.message || 'Failed to start upgrade'); setPolicyMigrationStatus('idle'); }
        } catch (e) { addPolicyLog('error', `Failed: ${e.message}`); setPolicyMigrationStatus('idle'); }
    };

    const getStatusIcon = (status) => {
        if (status === 'running') return <Loader className="spinning" size={18} />;
        if (status === 'completed') return <CheckCircle size={18} />;
        if (status === 'failed') return <XCircle size={18} />;
        return <Play size={18} />;
    };

    // ── Render ───────────────────────────────────────────────────────────────────
    return (
        <div className="root">
            <CustomHeaderWithOutAppBar />
            <main className="content">
                <div className="project-overview-wrap">
                    <Container className="project-overview-container">
                        <div className="mule-migration">
                            <div className="mule-container">
                                {/* ── Page Header + Mode Switcher ── */}
                                <div className="combined-header">
                                    <h1 className="mule-page-title">
                                        <Bot className="mule-page-title__icon" />
                                        Mule Upgrade Agent
                                    </h1>
                                    <div className="agent-mode-switcher">
                                        <button
                                            className={`mode-btn ${agentMode === AGENT_MODES.MULE_UPGRADE ? 'mode-btn--active' : ''}`}
                                            onClick={() => setAgentMode(AGENT_MODES.MULE_UPGRADE)}
                                        >
                                            <RefreshCw size={16} />
                                            Mule Upgrade
                                        </button>
                                        <button
                                            className={`mode-btn ${agentMode === AGENT_MODES.POLICY_UPGRADE ? 'mode-btn--active' : ''}`}
                                            onClick={() => setAgentMode(AGENT_MODES.POLICY_UPGRADE)}
                                        >
                                            <Shield size={16} />
                                            Policy Upgrade
                                        </button>
                                    </div>
                                </div>

                                {/* ══════════════ MULE UPGRADE PANEL ══════════════ */}
                                {agentMode === AGENT_MODES.MULE_UPGRADE && (
                                    <div className="mule-layout">
                                        {/* Left Sidebar */}
                                        <aside className="mule-sidebar">
                                            {/* Source Selection */}
                                            <section className="mule-card">
                                                <h2 className="mule-card__title"><Upload className="mule-card__icon" />Upgrade Source</h2>
                                                <div className="mule-source-types">
                                                    {[
                                                        { id: 'local', icon: <FolderOpen size={24} />, name: 'Local Directory', desc: 'Migrate from local folder' },
                                                        { id: 'git', icon: <Github size={24} />, name: 'Git Repository', desc: 'Single repo upgrade' },
                                                        { id: 'multi-git', icon: <FileText size={24} />, name: 'Multiple Repos', desc: 'Batch upgrade via list' },
                                                    ].map(s => (
                                                        <button key={s.id} onClick={() => handleSourceClick(s.id)} className={`mule-source-type ${sourceType === s.id ? 'mule-source-type--active' : ''}`}>
                                                            {s.icon}
                                                            <div className="mule-source-type__content">
                                                                <div className="mule-source-type__name">{s.name}</div>
                                                                <div className="mule-source-type__description">{s.desc}</div>

                                                            </div>
                                                        </button>
                                                    ))}
                                                </div>

                                                <div className="muleinput-section">
                                                    {sourceType === 'local' && (
                                                        <>
                                                            <input ref={fileInputRef} type="file" webkitdirectory="true" directory="true" multiple onChange={handleMuleFileUpload} style={{ display: 'none' }} />
                                                            {!uploadedFiles.length && !isUploading && !isAnalyzing && (
                                                                <button className="upload-btn" onClick={() => fileInputRef.current?.click()}><Upload size={20} />Select Folder to Upload</button>
                                                            )}
                                                            {isUploading && <div className="upload-status"><Loader className="spinner" size={20} /><span>Uploading {uploadedFiles.length} files...</span></div>}
                                                            {isAnalyzing && <div className="analysis-status"><Search className="spinner" size={20} /><span>Analyzing project with AI...</span></div>}
                                                            {uploadError && <div className="upload-error"><span>❌ {uploadError}</span><button onClick={() => fileInputRef.current?.click()}>Try Again</button></div>}
                                                            {uploadedFiles.length > 0 && !isUploading && serverPath && analysisComplete && (
                                                                <div className="upload-success">
                                                                    <div className="success-header"><CheckCircle size={24} color="#10b981" /><div className="success-info"><h4>Analysis Complete!</h4><p>{uploadedFiles.length} files analyzed</p></div></div>
                                                                    <button className="change-folder-btn" onClick={() => fileInputRef.current?.click()}>Change Folder</button>
                                                                </div>
                                                            )}
                                                        </>
                                                    )}
                                                    {sourceType === 'git' && (
                                                        <>
                                                            <div className="mule-form-group"><label className="mule-form-label">Repository URL</label><input type="text" value={gitUrl} onChange={e => { setGitUrl(e.target.value); if (e.target.value.trim()) { addMuleMessage(agentService.current.getScriptedResponse('git_url_set'), 'agent', 'success'); setCanStartMigration(true); } }} placeholder="https://github.com/user/repo.git" className="mule-form-input" /></div>
                                                            <div className="mule-form-group"><label className="mule-form-label">Branch</label><input type="text" value={gitBranch} onChange={e => setGitBranch(e.target.value)} placeholder="main" className="mule-form-input" /></div>
                                                        </>
                                                    )}
                                                    {sourceType === 'multi-git' && (
                                                        <div className="mule-form-group">
                                                            <label className="form-label">Repository List File</label>
                                                            <input type="file" accept=".txt" onChange={handleMuleFileUpload} className="mule-form-file-input" id="repo-file" />
                                                            <label htmlFor="repo-file" className="mule-form-file-label"><Upload className="mule-form-file-icon" />{repoListFile ? repoListFile.name : 'Upload repo-list.txt'}</label>
                                                        </div>
                                                    )}
                                                </div>
                                            </section>

                                            {/* Configuration */}
                                            {analysisComplete && (
                                                <section className="mule-card" ref={configRef}>
                                                    <button onClick={() => setShowConfig(p => !p)} className="mule-card__toggle">
                                                        <div className="mule-card__toggle-left"><Settings className="mule-card__icon" /><h2 className="mule-card__title">Configuration</h2></div>
                                                        <span className="mule-card__toggle-icon">{showConfig ? '−' : '+'}</span>
                                                    </button>
                                                    {showConfig && (
                                                        <div className="mule-config-content">
                                                            {dependencyAnalysis && <div className="config-notice"><AlertCircle size={16} /><span>Changing settings will require re-analysis</span></div>}
                                                            {[['targetRuntime', 'Target Runtime'], ['targetJava', 'Target Java']].map(([k, l]) => (
                                                                <div key={k} className="mule-form-group"><label className="mule-form-label">{l}</label><input type="text" value={config[k]} onChange={e => handleConfigChange(k, e.target.value)} className="mule-form-input" /></div>
                                                            ))}
                                                            {sourceType !== 'multi-git' && (
                                                                <>
                                                                    <div className="mule-form-group"><label className="mule-form-label">Destination Github URL</label><input type="text" value={config.destinationGitUrl} onChange={e => handleConfigChange('destinationGitUrl', e.target.value)} placeholder="https://github.com/user/repo.git" className="mule-form-input" /></div>
                                                                    <div className="mule-form-group"><label className="mule-form-label">Branch</label><input type="text" value={config.targetBranch} onChange={e => handleConfigChange('targetBranch', e.target.value)} className="mule-form-input" /></div>
                                                                </>
                                                            )}
                                                            <div className="mule-form-checkboxes">
                                                                {[['skipTests', 'Skip Tests'], ['dynamicVersions', 'Dynamic Version Resolution'], ['cleanupClones', 'Cleanup Clones']].map(([k, l]) => (
                                                                    <label key={k} className="mule-form-checkbox"><input type="checkbox" checked={config[k]} onChange={e => handleConfigChange(k, e.target.checked)} /><span>{l}</span></label>
                                                                ))}
                                                            </div>
                                                            {!dependencyAnalysis && (
                                                                <button onClick={analyzeDependencies} disabled={analyzingDependencies} className={`mule-btn-analyze ${analyzingDependencies ? 'analyzing' : ''}`}>
                                                                    {analyzingDependencies ? <><Loader className="spinner" size={20} />Analyzing...</> : <><Search size={20} />Analyze Dependencies</>}
                                                                </button>
                                                            )}
                                                        </div>
                                                    )}
                                                </section>
                                            )}

                                            {/* Start Button */}
                                            <button
                                                onClick={startMuleMigration}
                                                disabled={migrationStatus === 'running' || !wsConnected || isAnalyzing}
                                                className={`mule-btn-start ${canStartMigration && migrationStatus !== 'running' && wsConnected && !isAnalyzing ? 'mule-btn-start--enabled' : 'mule-btn-start--disabled'}`}
                                            >
                                                {isAnalyzing ? <><Search className="mule-btn-start__icon spinning" />Analyzing...</> : <><Play className="mule-btn-start__icon" />{migrationStatus === 'running' ? 'Upgrade in Progress...' : 'Start Upgrade'}</>}
                                            </button>
                                        </aside>

                                        {/* Right Panel */}
                                        <main className="mule-main-content">
                                            <aside className="mule-sidebar">
                                                {summary && (
                                                    <div className="mule-summary-grid">
                                                        {[['Total Projects', summary.total], ['Success', summary.success], ['Failed', summary.failed], ['Duration', `${summary.duration}s`]].map(([l, v]) => (
                                                            <div key={l} className="mule-summary-card"><div className="mule-summary-card__label">{l}</div><div className="mule-summary-card__value">{v}</div></div>
                                                        ))}
                                                    </div>
                                                )}
                                                {projects.length > 0 && (
                                                    <section className="mule-card">
                                                        <h2 className="mule-card__title">Migrated Projects</h2>
                                                        <div className="mule-projects-list">
                                                            {projects.map((p, i) => (
                                                                <div key={i} className="mule-project-item">
                                                                    <div className="mule-project-item__main">
                                                                        {p.status === 'pending' && <Clock className="mule-project-item__icon mule-project-item__icon--pending" />}
                                                                        {p.status === 'success' && <CheckCircle className="mule-project-item__icon mule-project-item__icon--success" />}
                                                                        {p.status === 'failed' && <XCircle className="mule-project-item__icon mule-project-item__icon--failed" />}
                                                                        <div className="mule-project-item__content">
                                                                            <div className="mule-project-item__name">{p.name}</div>
                                                                            {p.archived && <div className="mule-project-item__path">Archived: upgraded-projects/archive/{p.name}</div>}
                                                                        </div>
                                                                    </div>
                                                                    <div className="mule-project-item__actions">
                                                                        {p.duration && <span className="mule-project-item__duration">{p.duration}s</span>}
                                                                        {p.status === 'success' && migrationStatus === 'completed' && (
                                                                            <button onClick={() => downloadProject(p.name)} className="mule-project-item__download"><Download className="mule-project-item__download-icon" /></button>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </section>
                                                )}

                                                {/* Agent Chat */}
                                                <section className="mule-card mule-chat-card">
                                                    <div className="mule-card__header">
                                                        <h2 className="mule-card__title"><MessageSquare className="mule-card__icon" />Agent Chat</h2>
                                                        <button onClick={() => setMessages([{ type: 'agent', content: 'Chat cleared. Ready to help!', timestamp: new Date().toLocaleTimeString() }])} className="mule-btn-clear-logs"><RefreshCw className="mule-btn-clear-logs__icon" /></button>
                                                    </div>
                                                    {migrationStatus === 'running' && (
                                                        <div className="migration-status migration-status--running">
                                                            <div className="migration-status__content"><Clock className="migration-status__icon spinning" /><span>Upgrade in progress...</span></div>
                                                        </div>
                                                    )}
                                                    <div className="agent-chat-container" style={{ height: `${logsHeight}px` }}>
                                                        {messages.map((msg, idx) => {
                                                            const isLast = idx === messages.length - 1;
                                                            const isProcessing = analyzingDependencies || migrationStatus === 'running';
                                                            return (
                                                                <div key={idx} className={`agent-message agent-message--${msg.logType || 'info'}`}>
                                                                    <div className={`mule-agent-avatar ${isLast && isProcessing ? 'spinning' : ''}`}><Bot size={20} /></div>
                                                                    <div className="agent-message__content">
                                                                        <div className="agent-message__header">
                                                                            <span className="agent-message__sender">{msg.type === 'user' ? 'You' : 'Agent'}</span>
                                                                            <span className="agent-message__timestamp">{msg.timestamp}</span>
                                                                        </div>
                                                                        <div className="agent-message__text">{msg.content}</div>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                        <div ref={messagesEndRef} />
                                                    </div>
                                                    {wsConnected && (
                                                        <div className="mule-chat-input-section">
                                                            <div className="mule-chat-input-wrapper">
                                                                <input
                                                                    type="text" value={inputMessage}
                                                                    onChange={e => setInputMessage(e.target.value)}
                                                                    onKeyPress={e => { if (e.key === 'Enter' && inputMessage.trim()) { handleUserMessage(inputMessage); setInputMessage(''); } }}
                                                                    placeholder={waitingForConfirmation ? "Type 'confirm' or 'cancel'..." : migrationStatus === 'running' ? 'Upgrade in progress...' : 'Type a message or command...'}
                                                                    className="mule-chat-input"
                                                                    disabled={migrationStatus === 'running'}
                                                                />
                                                                <button onClick={() => { if (inputMessage.trim()) { handleUserMessage(inputMessage); setInputMessage(''); } }} disabled={!inputMessage.trim() || migrationStatus === 'running'} className="mule-chat-send-btn"><Send size={20} /></button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </section>
                                            </aside>
                                        </main>
                                    </div>
                                )}

                                {/* ══════════════ POLICY UPGRADE PANEL ══════════════ */}
                                {agentMode === AGENT_MODES.POLICY_UPGRADE && (
                                    <div className="mule-layout">
                                        <aside className="mule-sidebar">
                                            {/* Config Panel */}
                                            <section className="mule-card policy-config-card">
                                                <button onClick={() => setShowPolicyConfig(p => !p)} className="mule-card__toggle">
                                                    <div className="mule-card__toggle-left">
                                                        <Settings className="mule-card__icon" />
                                                        <h2 className="mule-card__title" style={{ margin: 0 }}>
                                                            Configuration&nbsp;
                                                            {isPolicyConfigValid()
                                                                ? <span className="policy-valid-badge">✓</span>
                                                                : <span className="policy-warn-badge">⚠</span>}
                                                        </h2>
                                                    </div>
                                                    <span className="mule-card__toggle-icon">{showPolicyConfig ? '−' : '+'}</span>
                                                </button>

                                                {showPolicyConfig && (
                                                    <div className="policy-config-body">

                                                        {/* 1. Manifest File */}
                                                        <div className="mule-form-group">
                                                            <label className="mule-form-label">Manifest File *</label>
                                                            <div className="policy-manifest-row">
                                                                <input
                                                                    type="text"
                                                                    className="mule-form-input"
                                                                    placeholder="manifests/policy-manifest.yaml"
                                                                    value={manifestPath}
                                                                    onChange={e => setManifestPath(e.target.value)}
                                                                    disabled={policyMigrationStatus === 'running' || setupStatus === 'running'}
                                                                />
                                                                <label htmlFor="policy-file-upload" className="policy-upload-label">
                                                                    <Upload size={13} /> Upload
                                                                </label>
                                                                <input
                                                                    id="policy-file-upload"
                                                                    type="file"
                                                                    accept=".yaml,.yml,.csv"
                                                                    onChange={handlePolicyFileUpload}
                                                                    style={{ display: 'none' }}
                                                                    disabled={policyMigrationStatus === 'running' || setupStatus === 'running'}
                                                                />
                                                            </div>
                                                            {uploadedFilePath && (
                                                                <span className="policy-field-success">✓ {uploadedFilePath}</span>
                                                            )}
                                                        </div>

                                                        {/* 2. Anypoint Account dropdown */}
                                                        <div className="mule-form-group">
                                                            <label className="mule-form-label">Anypoint Account *</label>
                                                            <div className="policy-select-wrap">
                                                                <select
                                                                    className="mule-form-input policy-select"
                                                                    value={selectedMuleAccount}
                                                                    onChange={e => handleMuleAccountChange(e.target.value)}
                                                                    disabled={loadingAccounts || policyMigrationStatus === 'running' || setupStatus === 'running'}
                                                                >
                                                                    <option value="">
                                                                        {loadingAccounts ? '⏳ Loading accounts...' : '— Select Anypoint Account —'}
                                                                    </option>
                                                                    {muleAccounts.map(acc => (
                                                                        <option key={acc._id} value={acc._id}>
                                                                            {acc.muleOrg || acc.muleOrgId}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                                {loadingAccounts && <Loader className="policy-field-spinner spinning" size={14} />}
                                                            </div>
                                                            {selectedMuleAccount && (
                                                                <span className="policy-field-success">✓ Org ID: {orgId}</span>
                                                            )}
                                                        </div>

                                                        {/* 3. Environment — only after account selected */}
                                                        {selectedMuleAccount && (
                                                            <div className="mule-form-group">
                                                                <label className="mule-form-label">Environment *</label>
                                                                <div className="policy-select-wrap">
                                                                    <select
                                                                        className="mule-form-input policy-select"
                                                                        value={selectedEnvironment}
                                                                        onChange={e => handleEnvironmentChange(e.target.value)}
                                                                        disabled={loadingEnvironments || policyMigrationStatus === 'running' || setupStatus === 'running'}
                                                                    >
                                                                        <option value="">
                                                                            {loadingEnvironments
                                                                                ? '⏳ Fetching environments...'
                                                                                : environments.length === 0
                                                                                    ? '— No environments found —'
                                                                                    : '— Select Environment —'}
                                                                        </option>
                                                                        {environments.map(env => (
                                                                            <option key={env.id} value={env.name}>
                                                                                {env.isProduction ? '🚀' : '🏖️'} {env.name} ({env.type})
                                                                            </option>
                                                                        ))}
                                                                    </select>
                                                                    {loadingEnvironments && (
                                                                        <Loader className="policy-field-spinner spinning" size={14} />
                                                                    )}
                                                                </div>
                                                                {envError && (
                                                                    <div className="policy-field-error">
                                                                        ⚠️ {envError}
                                                                        <button onClick={() => fetchEnvironments(muleAccounts.find(a => a._id === selectedMuleAccount))}>
                                                                            Retry
                                                                        </button>
                                                                    </div>
                                                                )}
                                                                {selectedEnvironment && !loadingEnvironments && (
                                                                    <span className="policy-field-success">✓Env ID: {envId}</span>
                                                                )}
                                                            </div>
                                                        )}

                                                        {/* 4. Dry Run */}
                                                        <div className="mule-form-checkboxes">
                                                            <label className="mule-form-checkbox">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={dryRun}
                                                                    onChange={e => setDryRun(e.target.checked)}
                                                                    disabled={policyMigrationStatus === 'running' || setupStatus === 'running'}
                                                                />
                                                                <span>Dry Run (test mode)</span>
                                                            </label>
                                                        </div>

                                                    </div>
                                                )}
                                            </section>

                                            <button onClick={startPolicySetup} disabled={setupStatus === 'running' || !wsConnected || !isPolicyConfigValid()} className={`mule-btn-start ${setupStatus !== 'running' && wsConnected && isPolicyConfigValid() ? 'mule-btn-start--enabled' : 'mule-btn-start--disabled'}`}>
                                                {getStatusIcon(setupStatus)}
                                                <span className="mule-btn-start__text">{setupStatus === 'running' ? 'Setup in Progress...' : setupStatus === 'completed' ? 'Setup Complete' : 'Start Upgrade Setup'}</span>
                                            </button>

                                            <button onClick={startPolicyUpgrade} disabled={policyMigrationStatus === 'running'} className={`mule-btn-start ${policyMigrationStatus !== 'running' && wsConnected ? 'mule-btn-start--enabled' : 'mule-btn-start--disabled'}`}>
                                                {getStatusIcon(policyMigrationStatus)}
                                                <span className="mule-btn-start__text">{policyMigrationStatus === 'running' ? 'Upgrade in Progress...' : policyMigrationStatus === 'completed' ? 'Upgrade Complete' : 'Start Upgrade'}</span>
                                            </button>
                                        </aside>

                                        <main className="mule-main-content">
                                            <section className="mule-card mule-logs-card">
                                                <aside>
                                                    <div className="mule-card__header">
                                                        <h2 className="mule-card__title"><AlertCircle className="mule-card__icon" />Upgrade Logs</h2>
                                                        <button onClick={() => { setLogs([]); setPolicySummary(null); }} className="mule-btn-clear-logs"><RefreshCw className="mule-btn-clear-logs__icon" /></button>
                                                    </div>
                                                    {(policyMigrationStatus === 'running' || setupStatus === 'running') && (
                                                        <div className="migration-status migration-status--running">
                                                            <div className="migration-status__content"><Clock className="migration-status__icon spinning" /><span>{setupStatus === 'running' ? 'Setup in progress...' : 'Upgrade in progress...'}</span></div>
                                                        </div>
                                                    )}
                                                    <div className="mule-logs-container" style={{ maxHeight: 'calc(100vh - 350px)', overflowY: 'auto', minHeight: '400px' }}>
                                                        {logs.length === 0 ? (
                                                            <div className="mule-logs-empty">Logs will appear here when upgrade starts...</div>
                                                        ) : (
                                                            <>
                                                                {logs.map((log, idx) => (
                                                                    <div key={idx} className={`log-entry log-entry--${log.type}`}>
                                                                        <span className="log-entry__timestamp">[{log.timestamp}]</span>
                                                                        <span className="log-entry__message">{log.message}</span>
                                                                    </div>
                                                                ))}
                                                                <div ref={logsEndRef} />
                                                            </>
                                                        )}
                                                    </div>
                                                </aside>
                                            </section>
                                        </main>
                                    </div>
                                )}
                            </div>
                        </div>
                    </Container>
                </div>
            </main>
        </div>
    );
}